import React, { useEffect, useMemo, useState } from 'react';
import { MenuButton } from './MenuButton/MenuButton';
import { MenuDropdown } from './MenuDropdown/MenuDropdown';
import { MenuItemProps } from './MenuItem/MenuItem';
import { styles } from './Menu.css';
import { BaseMenuItem, MenuData, MenuItemWithSubMenu } from './types';
import { useTrackingContext } from '../Tracking';

export type MenuProps = {
  menuData: MenuData;
  isOpen: boolean;
  shouldShowCount?: boolean;
  onToggle: () => void;
  isAnnouncementBarActive?: boolean;
};

export const Menu = ({ menuData, isOpen, shouldShowCount, onToggle, isAnnouncementBarActive }: MenuProps) => {
  const [activeMenus, setActiveMenus] = useState<Array<MenuItemWithSubMenu<BaseMenuItem>>>([]);
  const { tracker } = useTrackingContext();

  useEffect(() => {
    if (!isOpen) {
      setActiveMenus([]);
    }
  }, [isOpen]);

  const handleClick = (menuItem: MenuItemWithSubMenu<BaseMenuItem>, depth: number) => {
    const updatedActiveMenus = activeMenus.slice(0, depth);
    updatedActiveMenus[depth] = menuItem;
    setActiveMenus(updatedActiveMenus);
  };

  const handleBack = () => {
    tracker.trackCustomEvent({
      event: `custom.menu.click`,
      group: tracker.group,
      type: 'back',
    });
    setActiveMenus(activeMenus.slice(0, activeMenus.length - 1));
  };

  const parseMenuItems = (menuItems: Array<MenuItemWithSubMenu<BaseMenuItem>>, depth: number): Array<MenuItemProps> => {
    return menuItems.map(menuItem => ({
      type: menuItem.type,
      label: menuItem.label || '',
      isBold: menuItem.isBold,
      leftIcon: menuItem.icon,
      count: shouldShowCount ? menuItem.count : undefined,
      shouldHideArrow: !menuItem.subMenu?.length,
      onClick: () => handleClick(menuItem, depth),
      isActive: activeMenus.includes(menuItem),
      href: menuItem.href,
      shouldOpenInNewWindow: menuItem.shouldOpenInNewWindow,
      subMenuItems: menuItem.subMenu ? parseMenuItems(menuItem.subMenu, depth + 1) : undefined,
      onBack: handleBack,
    }));
  };

  const menuItems = useMemo(() => parseMenuItems(menuData.menuItems, 0), [menuData, activeMenus]);
  return (
    <li
      className={styles.listItem}
      data-testid="navigation-menu-item"
      data-menu-item-label={menuData.label.toLowerCase().trim()}
    >
      <MenuButton
        label={menuData.label}
        isOpen={isOpen}
        onClick={() => (!menuData.href ? onToggle() : null)}
        href={menuData.href}
      />
      <MenuDropdown
        isOpen={isOpen}
        menuItems={menuItems}
        label={menuData.label}
        onBack={onToggle}
        isAnnouncementBarActive={isAnnouncementBarActive}
        trackingData={{ nesting: [menuData.label] }}
      />
    </li>
  );
};
